import { useEffect, useRef, useState } from 'react';
import { useAppData } from './contexts/AppDataContext.js';

/**
 * #474 — subscribe a page-level list query to the #148 heartbeat.
 *
 * Returns a counter that advances once per poll moment the caller should
 * refetch on. Use it as a fetch-effect dependency; a value of 0 means no tick
 * has been delivered yet, so an effect keyed on it can tell its first run
 * (mount) from a genuine background refresh.
 *
 * `paused` is the mid-mutation guard, and it DROPS ticks rather than queueing
 * them. That is deliberate: a promote/reject/approve already ends by calling
 * its own refetch(), so a queued tick would fire a second, redundant request
 * against data the mutation just refreshed — and worse, could land after that
 * refetch and reinstate the row the operator just actioned. Skipping is what
 * the issue asks for: "skip the tick while one is in flight and let the
 * mutation's own refetch() land instead."
 *
 * This lives in its own module rather than beside `dataTick` in
 * AppDataContext so that a page test which mocks `useAppData` exercises the
 * REAL subscription logic — an intra-module call would bypass the mock and
 * force every such test to either stand up a full provider or re-implement
 * the drop-while-paused rule in a stub, which would then be the thing under
 * test instead of this.
 */
export function usePollTick(paused = false): number {
  const { dataTick } = useAppData();
  const [tick, setTick] = useState(0);
  // The heartbeat value already accounted for, so flipping `paused` back to
  // false re-runs this effect without replaying the tick it skipped.
  const seen = useRef(dataTick);

  useEffect(() => {
    if (dataTick === seen.current) return;
    seen.current = dataTick;
    if (paused) return;
    setTick(t => t + 1);
  }, [dataTick, paused]);

  return tick;
}
